import { SIDE_PANEL_OVERHANG_WIDTH } from '../../../constants';
import panelTypeConfig from '../panel/panelTypeConfig';

const STACKED = 'stacked';

export default class PanelGroupModel {
  constructor(panelLimit, userInitiatedUpdate) {
    this.panelLimit = panelLimit;
    this.shouldFocus = userInitiatedUpdate;
  }

  calculatePanelPosition = (i, updatedPosition) => i * updatedPosition;

  calculateFinalPanelPosition(i, updatedPosition) {
    const siblingPosition = this.calculatePanelPosition(i - 1, updatedPosition);
    return siblingPosition + SIDE_PANEL_OVERHANG_WIDTH;
  }

  panelGroupInfo = () => ({
    panelCount: 0,
    type: '',
    displayPanels: [],
    calculatedPositions: [],
  });

  current = this.panelGroupInfo();

  previous = this.panelGroupInfo();

  get hasMultipleColumns() {
    return this.current.type !== STACKED;
  }

  calculatePositions(updatedPosition) {
    const positions = this.current.displayPanels
      .map((_, index, self) => {
        const allPanelsVisible = this.panelLimit === this.current.displayPanels.length;
        return (
          allPanelsVisible && index > 0 && index === (self.length - 1)
            ? this.calculateFinalPanelPosition(index, updatedPosition)
            : this.calculatePanelPosition(index, updatedPosition)
        );
      });

    return positions.map(i => `${-Math.abs(i)}px`).reverse();
  }

  createOlderPanelsProps(removedPanels, offset) {
    const olderPanels = removedPanels.filter(panel => panel.index < offset);
    return olderPanels.map((panel) => {
      const previousIndex = this.previous.displayPanels.findIndex(({ id }) => panel.id === id);
      const initialPosition = this.previous.calculatedPositions[previousIndex];
      return {
        panel,
        initialPosition,
        targetPosition: initialPosition,
        hasFocus: false,
        isOpen: true,
      };
    });
  }

  createDisplayPanelsProps(updatedPosition) {
    return this.current.displayPanels.map((panel, index, array) => {
      const targetPosition = this.current.calculatedPositions[index];
      let initialPosition;

      const previousIndex = this.previous.displayPanels.findIndex(({ id }) => id === panel.id);
      if (previousIndex > -1) {
        // use the previous position of the panel
        initialPosition = this.previous.calculatedPositions[previousIndex];
      } else if (this.previous.panelCount < this.current.panelCount) {
        // panel added - use the off-screen position
        initialPosition = `${updatedPosition}px`;
      } else if (
        this.previous.panelCount > this.current.panelCount
        && this.previous.displayPanels[index].type === panel.type
      ) {
        // panel(s) removed - use the position of the panel that this panel has replaced
        initialPosition = this.previous.calculatedPositions[index];
      } else {
        // panels switched - use the target position
        initialPosition = targetPosition;
      }

      return {
        panel,
        targetPosition,
        initialPosition,
        hasFocus: this.shouldFocus && index === (array.length - 1),
        hideContents: this.panelLimit === array.length && array.length > 1 && index === 0,
        isOpen: true,
      };
    });
  }

  createClosedPanelsProps(closedPanels) {
    return closedPanels.map((panel) => {
      const previousIndex = this.previous.displayPanels.findIndex(({ id }) => id === panel.id);
      const initialPosition = this.previous.calculatedPositions[previousIndex];
      return {
        panel,
        targetPosition: '100%',
        initialPosition,
        hasFocus: false,
        isOpen: false,
      };
    });
  }

  createStackedClosedProps(closedPanels) {
    return this.current.displayPanels.length === 0
      ? closedPanels.map(panel => ({
        panel,
        targetPosition: '100%',
        initialPosition: '0px',
        hasFocus: false,
        isOpen: false,
      })) : [];
  }

  getTelescopicProps = (removedPanels, offset, updatedPosition) => [
    ...this.createOlderPanelsProps(removedPanels, offset),
    ...this.createDisplayPanelsProps(updatedPosition),
  ];

  getStackedProps(panels, updatedPosition) {
    return panels.map((panel, index, self) => {
      const panelAlreadyOpen = this.previous.displayPanels.length > 0;
      const initialPosition = panelAlreadyOpen ? '0px' : `${updatedPosition}px`;
      const hasFocus = self.length - 1 === index;

      return {
        hasFocus,
        hideContents: !hasFocus,
        initialPosition,
        isOpen: true,
        targetPosition: '0px',
        type: this.current.type,
        panel,
      };
    });
  }

  applyPanels(userInitiatedUpdate, panels, offset = 0, updatedPosition) {
    this.shouldFocus = userInitiatedUpdate;
    this.previous.displayPanels = this.current.displayPanels;
    this.current.displayPanels = panels.slice(offset);

    this.previous.panelCount = this.current.panelCount;
    this.current.panelCount = panels.length;

    this.previous.calculatedPositions = this.current.calculatedPositions;
    this.current.calculatedPositions = this.calculatePositions(updatedPosition);

    this.previous.type = this.current.type;
    this.current.type = panels.length > 0 ? panelTypeConfig[panels[0].type]?.display : '';

    const removedPanels = this.previous.displayPanels.filter(
      panel => !this.current.displayPanels.includes(panel),
    );

    let props;
    switch (this.current.type) {
      case STACKED:
        props = this.getStackedProps(panels, updatedPosition);
        break;
      default:
        props = this.getTelescopicProps(removedPanels, offset, updatedPosition);
    }

    const closedPanels = removedPanels.filter(
      panel => panel.index >= offset + this.current.displayPanels.length,
    );

    let closedProps;
    switch (this.previous.type) {
      case STACKED:
        closedProps = this.createStackedClosedProps(closedPanels);
        break;
      default:
        closedProps = this.createClosedPanelsProps(closedPanels);
    }

    return [
      ...props,
      ...closedProps,
    ];
  }
}
